import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Modal, Form, Input, InputNumber, Switch, Tag, message, Popconfirm } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined, SyncOutlined } from '@ant-design/icons';
import { apiFetch, formatDateTime, formatDate } from '../api';

const BrandsList: React.FC = () => {
  const [brands, setBrands] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);
  const [form] = Form.useForm();

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<any[]>('/brands/');
      setBrands(data);
    } catch (error: any) {
      message.error(error.message || 'Не удалось загрузить бренды');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  const handleSubmit = async (values: any) => {
    // Не отправляем пустые поля, чтобы при редактировании не затирать токены/секрет,
    // которые в форму не подгружаются (например, App Secret не возвращается с сервера).
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, v]) => v !== '' && v !== undefined && v !== null)
    );
    try {
      if (editing) {
        await apiFetch(`/brands/${editing.id}`, { method: 'PUT', body: payload });
        message.success('Бренд обновлён');
      } else {
        await apiFetch('/brands/', { method: 'POST', body: payload });
        message.success('Бренд добавлен');
      }
      setModalVisible(false);
      setEditing(null);
      form.resetFields();
      fetchBrands();
    } catch (error: any) {
      message.error(error.message || 'Не удалось сохранить бренд');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await apiFetch(`/brands/${id}`, { method: 'DELETE' });
      message.success('Бренд удалён');
      fetchBrands();
    } catch (error: any) {
      message.error(error.message || 'Не удалось удалить бренд');
    }
  };

  const [fetchingId, setFetchingId] = useState<number | null>(null);
  const [refreshingId, setRefreshingId] = useState<number | null>(null);

  const handleRefreshToken = async (id: number) => {
    setRefreshingId(id);
    try {
      const res = await apiFetch<any>(`/brands/${id}/refresh-token`, { method: 'POST' });
      const parts: string[] = [];
      if (res.facebook) parts.push(`FB: ${res.facebook}`);
      if (res.instagram) parts.push(`IG: ${res.instagram}`);
      message.success(`Продление — ${parts.join('; ') || 'нет токенов'}`);
      fetchBrands();
    } catch (error: any) {
      message.error(error.message || 'Не удалось продлить токен');
    }
    setRefreshingId(null);
  };

  const handleFetch = async (id: number) => {
    setFetchingId(id);
    try {
      const res = await apiFetch<any>(`/brands/${id}/fetch`, { method: 'POST' });
      if (res.results) {
        // Несколько площадок (Instagram + TikTok): суммируем.
        const total = res.results.reduce((s: number, r: any) => s + (r.new_comments || 0), 0);
        const errs = res.results.filter((r: any) => r.error).map((r: any) => r.error);
        if (errs.length) message.warning(`Новых комментариев: ${total}. Ошибки: ${errs.join('; ').slice(0, 200)}`);
        else message.success(`Подтянуто с ${res.results.length} площадок, новых комментариев: ${total}`);
      } else if (res.error) {
        message.error(`Ошибка подтягивания: ${res.error}`);
      } else if (res.skipped) {
        message.warning(`Пропущено: ${res.skipped}`);
      } else if (res.hint) {
        message.error(res.hint);
      } else if (res.comment_fetch_errors) {
        message.warning(`Постов: ${res.media}, новых: ${res.new_comments}. Не удалось получить комментарии у ${res.comment_fetch_errors} постов: ${res.error_sample || ''}`);
      } else {
        message.success(`Постов: ${res.media}, с новыми комментариями: ${res.posts_with_new_comments}, новых комментариев: ${res.new_comments}`);
      }
      fetchBrands();
    } catch (error: any) {
      message.error(error.message || 'Не удалось подтянуть');
    }
    setFetchingId(null);
  };

  const showAddModal = () => {
    setEditing(null);
    form.resetFields();
    setModalVisible(true);
  };

  const showEditModal = (brand: any) => {
    setEditing(brand);
    form.setFieldsValue(brand);
    setModalVisible(true);
  };

  const tokenTag = (present: boolean) =>
    present ? <Tag color="green">есть</Tag> : <Tag color="default">нет</Tag>;

  const columns = [
    { title: 'Название', dataIndex: 'name', key: 'name' },
    { title: 'Instagram', dataIndex: 'instagram_username', key: 'instagram_username' },
    { title: 'TikTok', dataIndex: 'tiktok_username', key: 'tiktok_username' },
    { title: 'IG токен', key: 'ig_token', render: (_: any, r: any) => tokenTag(!!r.has_instagram_token) },
    { title: 'FB токен', key: 'fb_token', render: (_: any, r: any) => tokenTag(!!r.has_facebook_token) },
    {
      title: 'FB токен до', dataIndex: 'facebook_token_expires_at', key: 'fb_exp',
      render: (v?: string) => (v ? formatDate(v) : <span style={{ color: '#aaa' }}>—</span>),
    },
    {
      title: 'IG токен до', dataIndex: 'instagram_token_expires_at', key: 'ig_exp',
      render: (v?: string) => (v ? formatDate(v) : <span style={{ color: '#aaa' }}>—</span>),
    },
    { title: 'Страниц', dataIndex: 'instagram_posts_pages', key: 'instagram_posts_pages' },
    { title: 'Интервал, мин', dataIndex: 'check_interval_minutes', key: 'check_interval_minutes' },
    {
      title: 'Последняя проверка', dataIndex: 'last_checked_at', key: 'last_checked_at',
      render: (v?: string) => formatDateTime(v),
    },
    { title: 'Активно', dataIndex: 'is_active', key: 'is_active', render: (active: boolean) => <Switch checked={active} disabled /> },
    {
      title: 'Действия', key: 'actions', render: (_: any, record: any) => (
        <Space>
          <Button icon={<SyncOutlined />} size="small" loading={fetchingId === record.id} onClick={() => handleFetch(record.id)}>
            Подтянуть
          </Button>
          <Button size="small" loading={refreshingId === record.id} onClick={() => handleRefreshToken(record.id)}>
            Продлить токен
          </Button>
          <Button icon={<EditOutlined />} size="small" title="Редактировать" onClick={() => showEditModal(record)} />
          <Popconfirm title="Удалить бренд?" description="Токены бренда будут потеряны." okText="Удалить" cancelText="Отмена" okButtonProps={{ danger: true }} onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger title="Удалить" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal} style={{ marginBottom: 16 }}>
        Добавить бренд
      </Button>
      <Table columns={columns} dataSource={brands} rowKey="id" loading={loading} scroll={{ x: 'max-content' }}
        locale={{ emptyText: 'Брендов пока нет — нажмите «Добавить бренд» и впишите токены соцсетей.' }} />
      <Modal
        title={editing ? 'Редактировать бренд' : 'Новый бренд'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); form.resetFields(); }}
        footer={null}
        width={560}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="instagram_username" label="Instagram Username">
            <Input />
          </Form.Item>
          <Form.Item name="instagram_account_id" label="Instagram Account ID (Graph API)">
            <Input placeholder="IG Business Account ID" />
          </Form.Item>
          <Form.Item name="instagram_access_token" label="Instagram Access Token"
            extra={editing ? (editing.has_instagram_token ? '✓ токен задан — оставьте пустым, чтобы не менять' : 'токен не задан') : undefined}>
            <Input.Password placeholder="токен graph.instagram.com / graph.facebook.com" />
          </Form.Item>
          <Form.Item name="facebook_page_id" label="Facebook Page ID">
            <Input />
          </Form.Item>
          <Form.Item name="facebook_access_token" label="Facebook Access Token"
            extra={editing ? (editing.has_facebook_token ? '✓ токен задан — оставьте пустым, чтобы не менять' : 'токен не задан') : undefined}>
            <Input.Password placeholder="Page Access Token (graph.facebook.com)" />
          </Form.Item>
          <Form.Item name="facebook_app_id" label="Facebook App ID" tooltip="Нужен для автопродления FB-токена">
            <Input placeholder="ID приложения Facebook" />
          </Form.Item>
          <Form.Item name="facebook_app_secret" label="Facebook App Secret" tooltip="Нужен для автопродления FB-токена"
            extra={editing ? (editing.has_facebook_app_secret ? '✓ секрет задан — оставьте пустым, чтобы не менять' : 'секрет не задан') : undefined}>
            <Input.Password placeholder="секрет приложения Facebook" />
          </Form.Item>
          <Form.Item name="telegram_bot_token" label="Telegram Bot Token бренда"
            extra={editing ? (editing.has_telegram_bot ? '✓ токен задан — оставьте пустым, чтобы не менять' : 'токен не задан') : undefined}>
            <Input.Password placeholder="токен бота, с которого пишем ответственному" />
          </Form.Item>
          <Form.Item name="tiktok_username" label="TikTok Username">
            <Input placeholder="без @" />
          </Form.Item>
          <Form.Item name="tiktok_access_token" label="TikTok Access Token (Research API)"
            extra={editing ? (editing.has_tiktok_token ? '✓ токен задан — оставьте пустым, чтобы не менять' : 'токен не задан') : undefined}>
            <Input.Password placeholder="токен open.tiktokapis.com (нужен прокси)" />
          </Form.Item>
          <Form.Item name="instagram_posts_pages" label="Сколько страниц постов подтягивать" initialValue={1}>
            <InputNumber min={1} max={50} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="check_interval_minutes" label="Интервал проверки (минуты)" initialValue={5}>
            <InputNumber min={1} max={1440} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="is_active" label="Активно" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">Сохранить</Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default BrandsList;
